import LogoutIcon from "@mui/icons-material/Logout";
import React from "react";
import {
  SxProps,
  Stack,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  Button,
  useTheme,
  IconButton,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import TerminalIcon from "@mui/icons-material/Terminal";
import { useNavigate } from "react-router";
import { cacheService, userService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ResetReduxData as ResetReduxDataFilter } from "../../../Redux/Reducers/redux-slice-filters";
import { ResetReduxData as ResetReduxDataReactPlayer } from "../../../Redux/Reducers/redux-slice-react-player";
import { ResetReduxNewRequest } from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch } from "../../../Redux/redux-store";
import { routePaths } from "../../../Router/router";
import { AclMatch, UserHasAcls } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import AvatarProfilePicture from "../../Misc/AvatarProfilePicture/avatar-profile-picture";
import { ModalContext } from "../../Modals";
import ModalUserSettings, { ModalUserSettingsElementProps } from "../../Modals/ModalsUserSettings/modal-user-settings";
import ModalUserEmulate, { ModalUserEmulateProps } from "../../Modals/ModalUserEmulate/modal-user-emulate";
import { ResetBasket } from "../../../Redux/Reducers/redux-slice-basket";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTranslation } from "react-i18next";
export interface SidebarUserProps {
  sx?: SxProps;
  drawerOpen?: boolean;
  onUpdate: (open: boolean) => void;
}

const SidebarUser: React.FC<SidebarUserProps> = (props) => {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const { drawerOpen, onUpdate } = props;

  const user = cacheService.GetUserInfo();
  const app = cacheService.GetAppData();

  const emulating = cacheService.GetIsEmulate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOnSettingsClick = React.useCallback(() => {
    if (!openModal) return;

    openModal<ModalUserSettingsElementProps>(ModalUserSettings);
  }, [openModal]);

  const handleEmulateUserClick = React.useCallback(() => {
    if (!emulating) {
      if (!openModal) return;

      openModal<ModalUserEmulateProps>(ModalUserEmulate);
    } else {
      cacheService.UnsetEmulateUser();
      handleClose();

      enqueueCustomSnackbar(t("alert:success.comebackUnderPersonalIdentity"), "success");
    }
  }, [openModal, emulating]);

  const logout = () => {
    navigate(routePaths.login.path);
    userService.Logout();

    // Clear data
    cacheService.UnsetAuth();
    dispatch(ResetReduxDataReactPlayer());
    dispatch(ResetReduxDataFilter());
    dispatch(ResetReduxNewRequest());
    dispatch(ResetBasket());
    dispatch(ResetBasket());
  };

  return (
    <Stack
      p={drawerOpen ? 1.5 : "9px"}
      ml={drawerOpen ? "0px" : "1px"}
      direction="row"
      justifyContent={drawerOpen ? "space-between" : "center"}
      alignItems="center"
    >
      <Stack spacing={drawerOpen ? 1 : 1.5} direction={drawerOpen ? "row" : "column"} alignItems="center">
        {!drawerOpen && (
          <IconButton
            size="small"
            sx={{ p: 1, width: "32px", height: "32px" }}
            color="ghost"
            id="long-button"
            onClick={(e) => onUpdate(!drawerOpen)}
          >
            <KeyboardDoubleArrowRightIcon />
          </IconButton>
        )}
        <AvatarProfilePicture
          onClick={handleClick}
          userId={user?.id}
          userFirstName={user?.firstName}
          userLastName={user?.lastName}
          open={open}
        />
        {drawerOpen && (
          <Stack sx={{ opacity: drawerOpen ? 1 : 0 }}>
            <Typography variant="body1" fontWeight={500}>
              {user?.firstName}
            </Typography>
            <Typography variant="body2" fontWeight={500} whiteSpace="normal">
              {user?.lastName}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Box id="tutorial-uikit-demo-profile">
        {drawerOpen && (
          <IconButton
            size="small"
            sx={{ p: 1, width: "32px", height: "32px" }}
            color="ghost"
            id="long-button"
            onClick={(e) => onUpdate(!drawerOpen)}
          >
            <KeyboardDoubleArrowLeftIcon />
          </IconButton>
        )}
      </Box>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleOnSettingsClick}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {t("common:settings")}
        </MenuItem>
        {(UserHasAcls([AclTypes.aclLoginEmulate, AclTypes.aclSuperUser], AclMatch.OneAtLeast) || emulating) && (
          <MenuItem onClick={handleEmulateUserClick}>
            <ListItemIcon>
              <PeopleIcon fontSize="small" />
            </ListItemIcon>
            {!emulating ? t("menu:item.emulateUser") : t("menu:item.stopUserEmulate")}
          </MenuItem>
        )}
        <MenuItem onClick={logout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("common:logout")}
        </MenuItem>
        <MenuItem disabled={true}>
          <ListItemIcon>
            <TerminalIcon fontSize="small" />
          </ListItemIcon>
          V{app?.version}
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default SidebarUser;
